import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

const ErrorPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(15);
  const errorCode = searchParams.get('code') || 'default';
  const origin = searchParams.get('origin');

  if (!origin || origin !== 'app') {
    window.location.replace('/');
    return null;
  }

  const errorConfig: Record<string, any> = {
    invalid_portal_access: {
      code: 'invalid_portal_access',
      title: 'Access Denied',
      statusCode: 403,
      redirect: '/dashboard',
      btnLabel: 'Back to Main Site',
      message:
        'You do not have access to this portal. Please contact support for assistance.',
    },
    self_signup_not_allowed: {
      code: 'self_signup_not_allowed',
      title: 'Registration Restricted',
      statusCode: 403,
      redirect: '/dashboard',
      btnLabel: 'Back to Main Site',
      message:
        'Access denied: Direct signup is not allowed. Please contact support.',
    },
    user_not_create_error: {
      code: 'user_not_create_error',
      title: 'User Creation Error',
      statusCode: 500,
      redirect: '/dashboard',
      btnLabel: 'Back to Main Site',
      message:
        'An error occurred while creating your account. Please try again later. If the issue persists, contact support.',
    },
    invalid_token: {
      code: 'invalid_token',
      title: 'Authentication Error',
      statusCode: 401,
      redirect: '/dashboard',
      btnLabel: 'Back to Main Site',
      message:
        'We could not verify your identity. This might happen if your session is corrupted or invalid. Please log in again.',
    },
    unauthorized: {
      code: 'unauthorized',
      title: 'Unauthorized Access',
      statusCode: 403,
      redirect: '/dashboard',
      btnLabel: 'Back to Main Site',
      message: 'You are not authorized to access this resource.',
    },

    resource_no_longer_available: {
      code: 'resource_no_longer_available',
      title: 'This neighborhood isn’t on our map',
      statusCode: 404,
      redirect: '/home',
      btnLabel: 'Take Me Home',
      message:
        'One Platform. Every Service. Every Neighborhood. Except, apparently, this page — it moved, closed up shop, or never existed.',
    },
    default: {
      code: 'default',
      title: 'Unexpected Error',
      statusCode: 500,
      redirect: '/dashboard',
      btnLabel: 'Back to Main Site',
      message: 'An error occurred. Please try again.',
    },
  };

  const config = errorConfig[errorCode] || errorConfig.default;

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    if (countdown === 0) {
      if (config.redirect.startsWith('http')) {
        window.location.href = config.redirect;
      } else {
        navigate(config.redirect);
      }
    }
    return () => clearInterval(timer);
  }, [countdown, navigate, config.redirect]);

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-[#0f172a] px-6 py-12">
      {/* Dot grid pattern */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(circle, rgba(93,203,209,0.16) 1.5px, transparent 1.5px)',
          backgroundSize: '36px 36px',
          animation: 'hp-drift 14s ease-in-out infinite',
        }}
      />

      {/* Radial glow overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at 50% 30%, rgba(0,178,216,0.12), transparent 60%)',
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex max-w-[600px] flex-col items-center gap-[26px] text-center">
        {/* HP Pin / Drop icon (only for 404) */}
        {config.statusCode === 404 ? (
          <div className="relative flex h-24 w-[72px] items-start justify-center">
            {/* Shadow */}
            <div
              className="absolute bottom-[6px] h-[10px] w-[34px] rounded-[50%] bg-black/35"
              style={{ filter: 'blur(3px)' }}
            />
            {/* Pin body */}
            <div
              className="h-[60px] w-[60px] rounded-[50%_50%_50%_0] shadow-[0_8px_20px_rgba(0,178,216,0.35)]"
              style={{
                background: 'linear-gradient(135deg, #00CED8, #317690)',
                animation: 'hp-drop 1.1s cubic-bezier(0.34,1.56,0.64,1) both',
              }}
            >
              {/* Inner cutout */}
              <div className="absolute left-4 top-4 h-7 w-7 rounded-full bg-[#0f172a]" />
            </div>
            {/* Ping ring */}
            <div
              className="absolute bottom-[6px] h-[10px] w-[34px] rounded-[50%] border-2 border-[#00CED8]"
              style={{ animation: 'hp-ping 1.8s ease-out infinite' }}
            />
          </div>
        ) : (
          /* Generic status badge for non-404 errors */
          <div className="flex h-24 items-center justify-center">
            <span className="rounded-full border border-[#5DCBD1]/30 bg-[#5DCBD1]/10 px-5 py-2 font-mono text-sm font-semibold tracking-wider text-[#5DCBD1]">
              ERROR
            </span>
          </div>
        )}

        {/* Status Code */}
        <h1
          className="font-mono text-[120px] font-bold leading-none tracking-[-4px]"
          style={{
            background: 'linear-gradient(90deg, #40E0E3, #00B2D8)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            color: 'transparent',
          }}
        >
          {config.statusCode}
        </h1>

        {/* Title */}
        <h2 className="text-[30px] font-semibold leading-tight text-white">
          {config.title}
        </h2>

        {/* Message */}
        <p className="max-w-[460px] text-[15px] leading-relaxed text-[#5DCBD1]">
          {config.message}
        </p>

        {/* CTA Button */}
        <Link
          to={config.redirect}
          aria-live="polite"
          className="mt-1 inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#00CED8] to-[#00B2D8] px-6 py-3 text-sm font-medium text-white shadow-[0_4px_14px_rgba(0,178,216,0.35)] transition-all duration-200 hover:shadow-[0_6px_20px_rgba(0,178,216,0.5)] hover:brightness-110 active:scale-[0.98]"
        >
          {config.btnLabel}
        </Link>

        {/* Auto-redirect countdown */}
        <p className="-mt-3 text-xs tracking-wide text-[#5DCBD1]/60">
          Auto redirect in ({countdown}) seconds
        </p>

        {/* Branding */}
        <div className="mt-7 flex items-center gap-2 opacity-70">
          <img
            src="/HirePanther_Icon02.png"
            alt=""
            className="h-5 w-5 object-contain"
          />
          <span className="text-xs tracking-wider text-[#5DCBD1]">
            Hire Panther &middot; Error {config.statusCode}
          </span>
        </div>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes hp-drop {
          0% { transform: translateY(-40px) rotate(-45deg); opacity: 0; }
          60% { transform: translateY(6px) rotate(-45deg); opacity: 1; }
          80% { transform: translateY(-4px) rotate(-45deg); }
          100% { transform: translateY(0) rotate(-45deg); }
        }
        @keyframes hp-ping {
          0% { transform: scale(0.4); opacity: 0.55; }
          100% { transform: scale(2.6); opacity: 0; }
        }
        @keyframes hp-drift {
          0%, 100% { background-position: 0 0; }
          50% { background-position: 26px 18px; }
        }
      `}</style>
    </div>
  );
};

export default ErrorPage;
